import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    TablePagination,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import useTitle from "hooks/useTitle";
import { getMcaCompanyDetails } from "api/services/mca/mca";

interface CompanyResultItem {
    availability: string;
    companyLlpNumber: string;
    companyName: string;
    nameAvalibilityStatus: string;
}


const McaCompanyDetails = () => {
    useTitle("ATOM Pro | MCA");

    const [companyName, setCompanyName] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useQuery(
        ["get-mca-company-details"],
        () =>
            getMcaCompanyDetails({
                companyName,
            }),
        {
            enabled: false,
            retry: 1,
            refetchOnWindowFocus: false,
        }
    );


    const handleSearch = () => {
        if (!companyName) return;
        setPage(0);
        refetch();
    };


    const rows: CompanyResultItem[] = useMemo(() => {
        return data?.data ?? [];
    }, [data]);





    const paginatedRows = useMemo(() => {
        return rows.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [rows, page, rowsPerPage]);
    const apierror =
        data?.success === false;
    const isNoData =
        data?.success === true &&
        data?.count === 0;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                px: { xs: 2, md: 4 },
                py: 4,
                background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
            }}
        >
            {/* Header */}
            <Box textAlign="center" mb={5}>
                <Typography variant="h5" fontWeight={700}>
                    Check Company / LLP Details
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Search company name availability from MCA
                </Typography>
            </Box>

            {/* Glass Search Card */}
            <Paper
                sx={{
                    p: 4,
                    mb: 5,
                    borderRadius: "20px",
                    background: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255,255,255,0.4)",
                    boxShadow: `
          0 12px 40px rgba(0,0,0,0.08),
          inset 0 1px 2px rgba(255,255,255,0.6)
        `,
                }}
            >
                <Box
                    display="flex"
                    gap={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems="center"
                >
                    <TextField
                        fullWidth
                        label="Enter Company Name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSearch();
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "14px",
                                background: "rgba(255,255,255,0.6)",
                                backdropFilter: "blur(8px)",
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                        disabled={!companyName.trim() || isLoading}
                        sx={{
                            px: 4,
                            py: 2,
                            borderRadius: "14px",
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#fff",

                            background: "rgba(0,0,0,0.6)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(255,255,255,0.25)",

                            boxShadow: `
              0 6px 20px rgba(0,0,0,0.4),
              inset 0 1px 1px rgba(255,255,255,0.2)
            `,
                            transition: "all 0.35s ease",

                            "&:hover": {
                                background: "rgba(37, 99, 235, 0.75)",
                                // transform: "translateY(-2px)",
                                boxShadow: `
                0 12px 30px rgba(37,99,235,0.6),
                inset 0 1px 2px rgba(255,255,255,0.5)
              `,
                            },
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </Paper>

            {isLoading || isFetching ? (
                <Box textAlign="center" mt={5}>
                    <CircularProgress />
                </Box>
            ) : isError || apierror ? (
                <Alert
                    severity="error"
                    sx={{ borderRadius: "14px", backdropFilter: "blur(10px)" }}
                >
                    {data?.error?.message ||
                        data?.message ||
                        (error as any)?.message ||
                        "Failed to fetch MCA data. Try again later."}
                </Alert>
            ) : companyName && !isLoading && isNoData && !isError ? (
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: "14px",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    No filing records found. Reserve this name for <a href="https://www.mca.gov.in/content/mca/global/en/mca/e-filing/incorporation-change-services/spice.html" target="_blank" rel="noopener noreferrer">Company</a> or <a href="https://www.mca.gov.in/content/mca/global/en/mca/llp-e-filling/run-llp.html" target="_blank" rel="noopener noreferrer">LLP</a>
                </Alert>
            ) : !isLoading && rows.length > 0 && (
                <Paper
                    sx={{
                        borderRadius: "22px",
                        overflow: "hidden",
                        background: "rgba(255,255,255,0.25)",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        boxShadow: `
            0 15px 45px rgba(0,0,0,0.08),
            inset 0 1px 2px rgba(255,255,255,0.6)
          `,
                    }}
                >
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table stickyHeader>
                            <TableHead sx={{ background: "rgb(255, 255, 255)" }}>
                                <TableRow>
                                    {[
                                        "Company / LLP Number",
                                        "Company Name",
                                        "Availability",
                                        "Name Status",
                                    ].map((header, i) => (
                                        <TableCell
                                            key={i}
                                            sx={{
                                                fontWeight: 700,
                                                background: "rgba(255,255,255,0.6)",
                                                backdropFilter: "blur(6px)",
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedRows.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            transition: "0.3s",
                                            "&:hover": {
                                                background:
                                                    "rgba(37,99,235,0.08)",
                                            },
                                        }}
                                    >
                                        <TableCell>{row.companyLlpNumber || "-"}</TableCell>
                                        <TableCell>{row.companyName}</TableCell>
                                        <TableCell>{row.availability}</TableCell>
                                        <TableCell>{row.nameAvalibilityStatus}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={data?.count ?? 0}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(0);
                        }}
                    />

                </Paper>
            )}
        </Box>
    );

};

export default McaCompanyDetails;
